import { isAdminPermission, type AdminPermission } from '@carcheck/shared';

/**
 * Decoded shape of the admin access-token JWT payload we rely on for
 * session rehydration. Only the fields the SPA trusts are typed — the
 * token carries more claims (`iat`, `exp`, `role`, …) we intentionally
 * ignore here.
 */
export type DecodedAdminJwt = {
  sub: string;
  permissions: AdminPermission[];
};

/**
 * Base64url-decode the middle segment of a JWT and JSON-parse it.
 *
 * NO signature verification — the token has just arrived over TLS from
 * our own API (`/auth/refresh`), so we trust its integrity for the sole
 * purpose of reading `sub` + `permissions` to rehydrate `currentAdmin`.
 * We deliberately avoid a `jwt-decode` dependency (project rule #9) —
 * this is a ~15-line base64url decode.
 *
 * Returns `null` for any malformed input (missing segment, bad base64,
 * non-JSON, missing/blank `sub`). Permissions are filtered through the
 * canonical `isAdminPermission` guard so unknown wire strings are
 * dropped rather than smuggled into the SPA's `AdminPermission[]`.
 */
export const decodeJwtPayload = (token: string | null | undefined): DecodedAdminJwt | null => {
  if (!token) return null;

  const segment = token.split('.')[1];
  if (!segment) return null;

  const payload = decodeBase64UrlToJson(segment);
  if (payload === null) return null;

  const sub = payload.sub;
  if (typeof sub !== 'string' || sub.length === 0) return null;

  const permissions = Array.isArray(payload.permissions)
    ? payload.permissions.filter(
        (value): value is AdminPermission => typeof value === 'string' && isAdminPermission(value),
      )
    : [];

  return { sub, permissions };
};

/**
 * Decode a base64url string into a parsed JSON object, or `null` if the
 * segment is not valid base64url / not valid JSON / not an object.
 */
const decodeBase64UrlToJson = (segment: string): Record<string, unknown> | null => {
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = decodeBase64(padded);
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
};

/**
 * Base64 → UTF-8 string. Prefers the browser's `atob`; falls back to
 * `Buffer` (jsdom/node test envs where `atob` may be absent).
 */
const decodeBase64 = (input: string): string => {
  if (typeof atob === 'function') {
    const binary = atob(input);
    // Re-decode as UTF-8 so multi-byte chars in claims survive.
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  }
  return Buffer.from(input, 'base64').toString('utf-8');
};
